import { useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';

import { useAdminGetCategories } from '../../../categories/hooks/admin-get-categories.hook';
import { Category } from '../../../categories/types/category.type';
import { SubCategory } from '../../../categories/types/sub-category.type';
import { UpdateSubCategoryRequestDTO } from '../../../categories/types/dtos/update-sub-category.dto';
import {
  CATEGORY_NAME_MAX_LENGTH,
  CATEGORY_NAME_MIN_LENGTH,
} from '../../../categories/constants/category-name-length.constants';

const SUB_CATEGORY_NAME_INPUT_ID = 'edit-sub-category-name';
const SUB_CATEGORY_DESCRIPTION_INPUT_ID = 'edit-sub-category-description';

type Props = {
  open: boolean;
  subCategory: SubCategory;
  isSubmitting: boolean;
  onSubmit: (data: UpdateSubCategoryRequestDTO) => Promise<void> | void;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    minWidth: 400,
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
}));

export function EditSubCategoryDialog({ open, subCategory, isSubmitting, onSubmit, onClose }: Props) {
  const classes = useStyles();
  const { isLoading: isLoadingCategories, categories } = useAdminGetCategories();

  const [name, setName] = useState(subCategory.name);
  const [description, setDescription] = useState(subCategory.description);
  const [categoryId, setCategoryId] = useState(subCategory.category);

  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => !category.archivedAt)
        .sort((first, second) => first.name.localeCompare(second.name)),
    [categories]
  );
  const selectedCategory = categoryOptions.find((category) => category.id === categoryId) ?? null;

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();

  const changes: UpdateSubCategoryRequestDTO = {};
  if (trimmedName !== subCategory.name) changes.name = trimmedName;
  if (trimmedDescription !== subCategory.description) changes.description = trimmedDescription;
  if (categoryId !== subCategory.category) changes.category = categoryId;

  const hasChanges = Object.keys(changes).length > 0;
  const isValid =
    trimmedName.length >= CATEGORY_NAME_MIN_LENGTH && trimmedDescription !== '' && selectedCategory !== null;
  const canSubmit = hasChanges && isValid && !isSubmitting;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(changes);
  };

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} disableEscapeKeyDown={isSubmitting}>
      <DialogTitle>Modifier la sous-rubrique</DialogTitle>
      <DialogContent>
        <div className={classes.fields}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            id={SUB_CATEGORY_NAME_INPUT_ID}
            label="Nom"
            inputProps={{ maxLength: CATEGORY_NAME_MAX_LENGTH }}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Autocomplete
            size="small"
            fullWidth
            loading={isLoadingCategories}
            value={selectedCategory}
            options={categoryOptions}
            groupBy={(option) => option.name[0].toUpperCase()}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, selected) => option.id === selected.id}
            onChange={(event, selectedOption: Category | null) => setCategoryId(selectedOption?.id ?? '')}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="outlined" label="Rubrique" required />
            )}
          />
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            required
            multiline
            minRows={3}
            id={SUB_CATEGORY_DESCRIPTION_INPUT_ID}
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : null}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
